/**
 * Third-party script loader with performance optimization
 * Loads scripts asynchronously after critical content is ready
 */

(function() {
  'use strict';

  // Configuration for third-party scripts
  const thirdPartyScripts = {
    analytics: {
      src: 'https://www.googletagmanager.com/gtag/js',
      priority: 'high',
      delay: 1000,
      condition: () => !window.location.href.includes('/checkout')
    },
    facebook: {
      src: 'https://connect.facebook.net/en_US/fbevents.js',
      priority: 'medium',
      delay: 2000,
      condition: () => true
    },
    reviews: {
      src: 'https://productreviews.shopifycdn.com/assets/v4/spr.js',
      priority: 'low',
      delay: 3000,
      condition: () => document.querySelector('.product-reviews')
    }
  };

  // Script loading queue
  const loadQueue = {
    high: [],
    medium: [],
    low: []
  };

  // Check if user is on a slow connection
  function isSlowConnection() {
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (conn) {
      return conn.effectiveType === '2g' || conn.effectiveType === 'slow-2g' || conn.saveData === true;
    }
    return false;
  }

  // Load a script asynchronously
  function loadScript(config) {
    return new Promise((resolve, reject) => {
      if (!config.condition || !config.condition()) {
        resolve('Skipped: condition not met');
        return;
      }

      const script = document.createElement('script');
      script.src = config.src;
      script.async = true;
      script.defer = true;

      script.onload = () => resolve('Loaded: ' + config.src);
      script.onerror = () => reject('Failed: ' + config.src);

      // Add to DOM
      document.body.appendChild(script);
    });
  }

  // Process the load queue by priority
  async function processQueue() {
    // Skip or delay on slow connections
    if (isSlowConnection()) {
      console.log('Slow connection detected, delaying third-party scripts');
      await new Promise(resolve => setTimeout(resolve, 5000));
    }

    // Load high priority scripts first
    for (const script of loadQueue.high) {
      try {
        await loadScript(script);
        await new Promise(resolve => setTimeout(resolve, script.delay || 0));
      } catch (error) {
        console.error('Script loading error:', error);
      }
    }

    // Load medium priority scripts
    if (window.requestIdleCallback) {
      window.requestIdleCallback(() => {
        loadQueue.medium.forEach(script => {
          setTimeout(() => loadScript(script), script.delay || 0);
        });
      });
    } else {
      setTimeout(() => {
        loadQueue.medium.forEach(script => {
          setTimeout(() => loadScript(script), script.delay || 0);
        });
      }, 2000);
    }

    // Load low priority scripts last
    if (window.requestIdleCallback) {
      window.requestIdleCallback(() => {
        loadQueue.low.forEach(script => {
          setTimeout(() => loadScript(script), script.delay || 0);
        });
      }, { timeout: 5000 });
    } else {
      setTimeout(() => {
        loadQueue.low.forEach(script => {
          setTimeout(() => loadScript(script), script.delay || 0);
        });
      }, 5000);
    }
  }

  // Categorize scripts by priority
  function categorizeScripts() {
    Object.entries(thirdPartyScripts).forEach(([key, config]) => {
      if (config.condition && config.condition()) {
        loadQueue[config.priority].push(config);
      }
    });
  }

  // Initialize when DOM is ready
  function init() {
    // Wait for critical content
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
      return;
    }

    // Further delay to ensure main content is rendered
    setTimeout(() => {
      categorizeScripts();

      // Start loading after user interaction or timeout
      let hasInteracted = false;
      const startLoading = () => {
        if (!hasInteracted) {
          hasInteracted = true;
          processQueue();
        }
      };

      // Load on first user interaction
      ['mousedown', 'keydown', 'touchstart', 'scroll'].forEach(event => {
        document.addEventListener(event, startLoading, { once: true, passive: true });
      });

      // Or load after a timeout
      setTimeout(startLoading, 5000);
    }, 1000);
  }

  // Google Analytics helper
  window.gtag = window.gtag || function() {
    (window.dataLayer = window.dataLayer || []).push(arguments);
  };

  // Facebook Pixel helper
  window.fbq = window.fbq || function() {
    (window.fbq.queue = window.fbq.queue || []).push(arguments);
  };

  // Start the process
  init();
})();